import type { ReactNode } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Image } from "../../../fe-ui/atoms/Image";

interface Props {
  body?: string;
  bgStyle?: 'primary' | 'secondary';
  children?: ReactNode;
  image?: string;
  isCentered?: boolean;
  jobTitle?: string;
  name?: string;
  title?: string;
  variant?: 'left' | 'right' | 'withBorder';
}

export const LandingQuote = ({
  body,
  bgStyle = 'primary',
  children,
  jobTitle,
  title,
  image,
  isCentered = false,
  name,
  variant = 'left',
}: Props) => {
  const theme = useTheme();
  const isWithBorderVariant = variant === 'withBorder';
  const bgPrimary =
    `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`;
  const bgSecondary =
    `linear-gradient(135deg, #355AA8 0%, ${theme.palette.secondary.main} 100%)`;

  const renderContent = () => (
    <>
      <Stack
        direction={"column"}
        spacing={2}
        sx={[
          isCentered && { textAlign: 'center' },
          { mb: 'auto' }
        ]}
      >
        {title ? <Typography variant={isWithBorderVariant ? 'h2' : 'h3'}
        >{title}</Typography> : null}
        {body ? (
          <Typography
            sx={{ mb: 'auto' }}
            variant="bodyLg"
          >{body}</Typography>
        ) : null}
        {children}
      </Stack>
      <Stack
        direction={"column"}
        spacing={0.5}
        sx={[
          isCentered && { textAlign: 'center' },
          { mt: 5 }
        ]}
      >
        {name ? (
          <Typography variant="h5">{name}</Typography>
        ) : null}
        {jobTitle ? (
          <Typography variant="body1">{jobTitle}</Typography>
        ) : null}
      </Stack>
    </>
  )

  if (isWithBorderVariant) {
    return (
      <Box py={5}>
        <Stack
          direction="column"
          spacing={7}
          sx={{
            borderRadius: 3,
            border: `solid 4px ${theme.palette.primary.main}`,
            pb: { xs: 3, md: 5 },
            pt: { xs: 4, md: 8 },
            px: { xs: 4, md: 8 },
          }}
        >
          {renderContent()}
        </Stack>
      </Box>
  )}

  return (
    <Box py={5}>
      <Stack
        direction={{ xs: "column", md: variant === 'left' ? "row" : "row-reverse" }}
        spacing={3}
        sx={{
          color: "primary.contrastText",
          borderRadius: 3,
          background: bgStyle === 'secondary' ? bgSecondary : bgPrimary,
        }}
      >
        {image ? <Image
          alt=""
          draggable={false}
          src={image}
          sx={{
            alignSelf: { xs: 'center', md: 'flex-end' },
            flexShrink: 0,
            flexBasis: { md: '50%', lg: '40%' },
            lineHeight: 0,
          }}
        /> : null}
        <Stack
          direction={"column"}
          sx={{
            py: { xs: 4, md: 8 },
            paddingInlineEnd: {xs: 4, md: variant === 'left' ? 8 : 0},
            paddingInlineStart: {xs: 4, md: variant === 'left' ? 0 : 8},
          }}
        >
          {renderContent()}
        </Stack>
      </Stack>
    </Box>
  );
};
